import { defineComponent as _defineComponent } from 'vue'
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";
import VRecipeNutritionLabelPrintout from "@/components/vRecipeNutritionLabelPrintout.vue";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import {DishRecipeCommon} from "@/models/DishRecipeCommon";
import {LabelRenderDto} from "@/models/NutritionLabels/v2/RenderLabelDto";
import {useAccountState} from "@/hooks/useAccountState";
import {computed, onMounted, provide, Ref, ref} from "vue";
import {SaveCustomizedLabelDataDto} from "@/models/NutritionLabels/v2/SaveCustomizedLabelDataDto";
import LabelPreviewImage from "./LabelPreviewImage.vue";
import VBackOfPackageConfig from "./vBackOfPackageConfig.vue";
import ExportLabelButtons from "./ExportLabelButtons.vue";
import VFrontOfPackageConfig from "@/components/vFrontOfPackageConfig.vue";
import VCautionaryFOPConfig from "@/components/vCautionaryFOPConfig.vue";
import VCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {CautionaryFoodType} from "@/models/Cautionary/CautionaryFoodType";
import {CaffeinatedProductType} from "@/models/Cautionary/CaffeinatedProductType";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {ExportLabelState2} from "@/models/NutritionLabels/v2/ExportLabelState2";
import {useNotify} from "@/hooks/useNotify";
import {useTranslations} from "@/hooks/useTranslations";
import RecipesRepository from "@/utils/API/RecipesRepository";
import { isSupplementLabelTemplate } from "@/utils/exportLabelRules";
import AllergenRepository from "@/utils/API/AllergenRepository";
import {AllergenConfigurationTargetType} from "@/models/Allergen/AllergenConfigurationData";

type ExportLabelProps = {
  recipe?: DishRecipeCommon;
  packageLabelType?: LabelTypeMetadata | LabelTypeMetadata.NutritionFacts;
  subrecipeServingSizes: RecipeCompositionMeasurement[];
  printMeta: PrintRecipeDetails;
  preselectedLabelType: LabelTypeMetadata;
  highIn?: CanadaFOPHighInMetadata | CanadaFOPHighInMetadata.None;
  roundingRuleIndex: number;
  subrecipeMeasurements: RecipeCompositionMeasurement[];
  containsAllergensText?: string;
  shouldTrackTrialActivity?: boolean;
  aiTargetType?: "recipe" | "combined-recipe";
}


export default /*#__PURE__*/_defineComponent({
  __name: 'vExportLabel',
  props: {
    recipe: null,
    packageLabelType: null,
    subrecipeServingSizes: { default: () => [] },
    printMeta: null,
    preselectedLabelType: { default: LabelTypeMetadata.NutritionFacts },
    highIn: null,
    roundingRuleIndex: { default: 0 },
    subrecipeMeasurements: { default: () => [] },
    containsAllergensText: { default: "" },
    shouldTrackTrialActivity: { type: Boolean, default: false },
    aiTargetType: { default: "recipe" }
  },
  emits: ["update-metadata", "trial-label-downloaded"],
  setup(__props: any, { emit }) {

const props = __props as { recipe?: DishRecipeCommon, packageLabelType?: LabelTypeMetadata | LabelTypeMetadata.NutritionFacts, subrecipeServingSizes: RecipeCompositionMeasurement[], printMeta: PrintRecipeDetails, preselectedLabelType: LabelTypeMetadata, highIn?: CanadaFOPHighInMetadata | CanadaFOPHighInMetadata.None, roundingRuleIndex: number, subrecipeMeasurements: RecipeCompositionMeasurement[], containsAllergensText: string, shouldTrackTrialActivity: boolean, aiTargetType: "recipe" | "combined-recipe" };





const notify = useNotify();
const accountState = useAccountState();
const translations = useTranslations();

const exportLabelState = ref<ExportLabelState2>({
  nutritionCountry: props.recipe.nutritionCountry,
  labelLanguage: LabelLanguage.English,
  labelPreview: true,
  disableDownload: false,
  hasValidCautionary: false,
  labelTypes: {
    [LabelTypeMetadata.CautionarySideOfPack]: {
      foodType: CautionaryFoodType.None,
      caffeinatedType: CaffeinatedProductType.None,
      quantityInPackage: 1,
      labelWidth: 1
    }
  },
});
const canPrint = ref(false);

const bopConfigRef = ref(null);
const fopConfigRef = ref(null);
const cfopConfigRef = ref(null);
const csopConfigRef = ref(null);
const buttonsRef = ref(null);

const fetchedLabel: Ref<SaveCustomizedLabelDataDto | null> = ref(null);
const labelPreviewBlob: Ref<Blob | null> = ref(null);
const activeView = ref(ExportLabelView.BOP);
const previewAbortController = ref(new AbortController());
const isSupplementLabel = ref(false);
const isLabelLoading = ref(false);
const labelLoadErrorTranslation = ref<string>("");
const savedLabelId = ref(null);
const isCautionaryEligible = ref(false);

const shouldDisplayLabel = computed(() => labelPreviewBlob.value !== null);

onMounted(async() => {
  await fetchSavedLabel();
});

function onCsopDataChanged(data: any) {
  exportLabelState.value.labelTypes = data;
}

function handleLanguageChange(language: LabelLanguage) {
  exportLabelState.value.labelLanguage = language;
}

/**
 * Fetches the saved label data for a specific recipe and initializes ingredient and sub-recipe displays.
 *
 * @return {Promise<void>} A promise that resolves when the label data is fetched and processed. If an error occurs during the fetch, it will be logged to the console.
 */
async function fetchSavedLabel() {
  try {
    const response = await NutritionLabelRepository.getLabelConfigurationV2(props.recipe.id);
    if (!response?.data) {
      return;
    }

    fetchedLabel.value = response.data;
    savedLabelId.value = fetchedLabel.value.id;
    bopConfigRef.value.savedLabelConfig = fetchedLabel.value;
  }
  catch (e) {
    console.error(e);
  }
  finally {
    if (typeof bopConfigRef.value?.onSavedLabelLookupCompleted === "function") {
      await bopConfigRef.value.onSavedLabelLookupCompleted();
    }
  }
}

/**
 * Fetches a preview of the label based on the provided render data.
 * @param renderData
 * @param requestingScreen
 */
async function fetchPreview(renderData: LabelRenderDto, requestingScreen: ExportLabelView) {
  const currentPageKey = buttonsRef.value?.viewManager?.currentPageKey;

  if (!currentPageKey) {
    return;
  }

  // Sanity check
  if (requestingScreen !== currentPageKey) {
    return;
  }

  isLabelLoading.value = true;
  canPrint.value = false;

  const request = {
    restaurantId: accountState.value.userDetails.restaurantId,
    shouldIncreaseDownloadedLabelsCount: false,
    roundingRuleIndex: props.roundingRuleIndex,
    labels: [renderData]
  };

  try {
    const response = await NutritionLabelRepository.downloadLabelV2(request);

    if (response !== undefined) {
      labelPreviewBlob.value = response.data;

      labelLoadErrorTranslation.value = "";
      isLabelLoading.value = false;
      return response.data;
    }
  }

  catch (ex: any) {
    isLabelLoading.value = false;

    if (ex.response.headers["content-type"].includes("application/json")) {
      const text = await ex.response.data.text();
      const json = JSON.parse(text);
      labelLoadErrorTranslation.value = json.message;
      return;
    }

    labelLoadErrorTranslation.value = ex.response?.data?.message || ex.message;
  }
}
provide("fetchPreview", fetchPreview);
provide("bopConfigRef", bopConfigRef);
provide("fopConfigRef", fopConfigRef);
provide("cfopConfigRef", cfopConfigRef);
provide("csopConfigRef", csopConfigRef);

function print() {
  window.print();
}

async function saveCustomizedLabel(saveData: SaveCustomizedLabelDataDto) {
  const response = await NutritionLabelRepository.saveLabelConfigurationV2(saveData);
  notify.success(translate("labels_save_success"));
  return response.data;
}

async function saveRecipeAllergenConfiguration(allergens: unknown[], mayContainText: Partial<Record<LabelLanguage, string[]>>) {
  await AllergenRepository.saveAllergenConfiguration({
    targetType: AllergenConfigurationTargetType.Recipe,
    targetId: props.recipe.id,
    allergens: Array.isArray(allergens) ? allergens : [],
    mayContainText: mayContainText || {}
  });
}

function handleCautionaryEligibility(isEligible: boolean) {
  isCautionaryEligible.value = isEligible;
}

function updateRecipeMetadata(isSupplement: boolean) {
  isSupplementLabel.value = isSupplement;
}

function translate(key: string) {
  return translations.value.get(key);
}

async function saveBopLabel() {
  if (!bopConfigRef.value) return;

  const config = bopConfigRef.value.renderModel;
  const belowLabelConfiguration = config?.renderModel?.belowLabelConfiguration || {};
  const mayContainText = bopConfigRef.value.mayContainText || {};

  updateIsSupplementLabel(config.labelName);

  await saveRecipeAllergenConfiguration(
    belowLabelConfiguration.allergens,
    mayContainText
  );

  // Build the DTO
  const saveData = {
    id: savedLabelId.value,
    recipeId: props.recipe.id,
    country: config.country,
    templateName: config.labelName,
    modelData: { ...config.renderModel }
  };

  savedLabelId.value = await saveCustomizedLabel(saveData);
}

function updateIsSupplementLabel(selectedTemplateName?: string){
  if (selectedTemplateName) {
    isSupplementLabel.value = isSupplementLabelTemplate(
      selectedTemplateName,
      props.preselectedLabelType
    );
  }

  emit("update-metadata", isSupplementLabel.value);
  RecipesRepository.UpdateMetaDataIsSupplementLabel(props.recipe.id, isSupplementLabel.value);
}

/**
 * Handles the transition to a new view by setting the active view.
 *
 * @param {ExportLabelView} nextView - The view to switch to.
 * @return {void} This method does not return a value.
 */
function handleSwitchView(nextView: ExportLabelView) {
  activeView.value = nextView;
}

/**
 * Loads the FOP label creator by setting the active view to the FOP export label view.
 *
 * @return {void} This function does not return a value.
 */
function loadFOPLabelCreator() {
  activeView.value = ExportLabelView.FOP;
}

/**
 * Sets the active view to the BOP label export view.
 *
 * @return {void} Does not return a value.
 */
function loadBOPLabelCreator() {
  activeView.value = ExportLabelView.BOP;
}


return { __sfc: true,props, emit, notify, accountState, translations, exportLabelState, canPrint, bopConfigRef, fopConfigRef, cfopConfigRef, csopConfigRef, buttonsRef, fetchedLabel, labelPreviewBlob, activeView, previewAbortController, isSupplementLabel, isLabelLoading, labelLoadErrorTranslation, savedLabelId, isCautionaryEligible, shouldDisplayLabel, onCsopDataChanged, handleLanguageChange, fetchSavedLabel, fetchPreview, print, saveCustomizedLabel, saveRecipeAllergenConfiguration, handleCautionaryEligibility, updateRecipeMetadata, translate, saveBopLabel, updateIsSupplementLabel, handleSwitchView, loadFOPLabelCreator, loadBOPLabelCreator, LabelTypeMetadata, ExportLabelView, VRecipeNutritionLabelPrintout, LabelPreviewImage, VBackOfPackageConfig, ExportLabelButtons, VFrontOfPackageConfig, VCautionaryFOPConfig, VCautionaryInputs, CollapsableCard }
}

})